import { ROUTES } from "@config";
import {
  useDeleteDocumentMutation,
  useDeleteDocumentVersionMutation,
  useGetUser,
} from "@hooks";
import { Document, DocumentVersion } from "@models";
import { Template } from "@types";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { DocumentDeleteConfirmationContent } from "src/components/Documents/DocumentDeleteConfirmationContent";
import { RenameDocumentDialog } from "src/components/Documents/RenameDocumentDialog";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import {
  canUserDeleteDraft,
  captureSentryMessage,
  fillRouteWithSlugs,
  getDocumentNavigationUrl,
} from "../../../../../utils";
import {
  DocViewerMoreOptions,
  MoreOptionsButton,
} from "./DocViewerMoreOptions";
import { useDocumentConfigAction } from "./useDocumentConfigAction";

const getDocVersionIdOlderThanCurrentVersion = (
  selectedDoc: Document,
  selectedVersionId: string
) => {
  if (selectedDoc.versions.length === 1) {
    return null;
  }

  const idx = selectedDoc.versions.findIndex((v) => v.id === selectedVersionId);
  if (idx === -1 || idx + 1 >= selectedDoc.versions.length) return null;

  return selectedDoc.versions[idx + 1].id;
};

export const NonDiffMoreOptions = ({
  selectedDoc,
  selectedVersion,
  isDocEnabled,
  handleFileDownload,
  templateConfig,
}: {
  selectedDoc: Document;
  selectedVersion: DocumentVersion;
  isDocEnabled: boolean;
  handleFileDownload?: () => void;
  templateConfig?: Template;
}) => {
  const { data: user } = useGetUser();
  const { orgId = "", deviceId = "" } = useParams();
  const navigate = useNavigate();
  const deleteDocumentVersionMutation = useDeleteDocumentVersionMutation();
  const deleteDocumentMutation = useDeleteDocumentMutation();

  const [deleteVersionModalOpen, setDeleteVersionModalOpen] = useState(false);
  const [deleteDocumentModalOpen, setDeleteDocumentModalOpen] = useState(false);
  const [renameDocumentModalOpen, setRenameDocumentModalOpen] = useState(false);

  if (!user) {
    captureSentryMessage("User not found to display DocViewerTopBar", {
      selectedDoc,
      selectedVersion,
    });
    return null;
  }

  const isAdmin = !!user.permissions?.includes("write:configuration");
  const canEditConfig =
    isAdmin &&
    !!templateConfig &&
    templateConfig.organizationId === orgId &&
    templateConfig.deviceId === deviceId;

  const { configButton, configModal } = useDocumentConfigAction({
    canEditConfig,
    deviceId,
    orgId,
    selectedDoc,
    templateConfig,
  });

  const handleDeleteVersion = async () => {
    setDeleteVersionModalOpen(false);

    const docVersionIdBeforeCurrentVersion =
      getDocVersionIdOlderThanCurrentVersion(selectedDoc, selectedVersion.id);

    await deleteDocumentVersionMutation.mutateAsync({
      orgId,
      deviceId,
      docId: selectedDoc.id,
      versionId: selectedVersion.id,
    });

    if (docVersionIdBeforeCurrentVersion) {
      navigate(
        getDocumentNavigationUrl(
          orgId,
          deviceId,
          selectedDoc.id,
          docVersionIdBeforeCurrentVersion
        )
      );
    } else {
      navigate(
        fillRouteWithSlugs(ROUTES.QMS, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
        })
      );
    }
  };

  const handleDeleteDocument = async () => {
    setDeleteDocumentModalOpen(false);

    await deleteDocumentMutation.mutateAsync({
      orgId,
      deviceId,
      docId: selectedDoc.id,
    });

    navigate(
      fillRouteWithSlugs(ROUTES.QMS, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: deviceId,
      })
    );
  };

  const hasLiveVersion = selectedDoc.versions.some(
    (version) => version.revision !== null
  );

  const shouldDisableDocumentDelete = hasLiveVersion;

  const shouldDisableDocumentVersionDelete =
    deleteDocumentVersionMutation.isPending ||
    !canUserDeleteDraft(selectedVersion);

  const normalButtons: MoreOptionsButton[] = [
    {
      title: "Rename document",
      onClick: () => setRenameDocumentModalOpen(true),
      disabled: false,
    },
    ...(configButton ? [configButton] : []),
    ...(handleFileDownload
      ? [
          {
            title: "Download File",
            onClick: handleFileDownload,
            disabled: false,
          },
        ]
      : []),
  ];

  const dangerZoneButtons: MoreOptionsButton[] = [
    {
      title: "Delete draft",
      onClick: () => setDeleteVersionModalOpen(true),
      disabled: shouldDisableDocumentVersionDelete,
    },
    {
      title: "Delete document",
      onClick: () => setDeleteDocumentModalOpen(true),
      disabled: shouldDisableDocumentDelete,
      tooltipTitle: shouldDisableDocumentDelete
        ? "Documents with live revisions cannot be deleted"
        : undefined,
    },
  ];

  return (
    <>
      <DocViewerMoreOptions
        normalButtons={normalButtons}
        dangerZoneButtons={dangerZoneButtons}
        isDisabled={!isDocEnabled}
        dataTestId="more-options-button"
      />

      <ConfirmationModal
        title="Delete draft"
        confirmText="Confirm"
        content="Are you sure you want to delete this draft?"
        onAbort={() => setDeleteVersionModalOpen(false)}
        onConfirm={handleDeleteVersion}
        open={deleteVersionModalOpen}
      />

      <ConfirmationModal
        title="Delete document"
        confirmText="Confirm"
        content={<DocumentDeleteConfirmationContent />}
        onAbort={() => setDeleteDocumentModalOpen(false)}
        onConfirm={handleDeleteDocument}
        open={deleteDocumentModalOpen}
      />
      {renameDocumentModalOpen && (
        <RenameDocumentDialog
          deviceId={selectedDoc.deviceId ?? deviceId}
          document={selectedDoc}
          onClose={() => setRenameDocumentModalOpen(false)}
          orgId={orgId}
        />
      )}
      {configModal}
    </>
  );
};

export default NonDiffMoreOptions;
